import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { scoreTone, SCORE_TONE_VALUE_CLASS, type ScoreTone } from '@/lib/score-visual';
import { cn } from '@/lib/utils';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary-active)]',
  secondary:
    'bg-[var(--color-surface-card)] text-[var(--color-ink)] border border-[var(--color-hairline)] hover:bg-[var(--color-surface-elevated)]',
  ghost: 'bg-transparent text-[var(--color-muted)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
  danger: 'bg-[var(--color-error)] text-white hover:opacity-90',
  success: 'bg-[var(--color-success)] text-white hover:opacity-90',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-5 text-sm',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex cursor-pointer items-center justify-center gap-2 rounded-[10px] font-semibold transition',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...props}
    />
  );
});

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          'h-10 w-full rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-card)] px-3.5 text-sm text-[var(--color-ink)]',
          'placeholder:text-[var(--color-muted)]',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-primary)]',
          'disabled:opacity-50',
          className,
        )}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        'w-full rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-card)] px-3.5 py-2.5 text-sm leading-relaxed text-[var(--color-ink)]',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-primary)]',
        'disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          'h-10 rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-card)] px-2.5 text-sm text-[var(--color-ink)]',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-primary)]',
          'disabled:opacity-50',
          className,
        )}
        {...props}
      />
    );
  },
);

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function Card(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl border border-[var(--color-hairline)] bg-[var(--color-surface-card)]',
        className,
      )}
      {...props}
    />
  );
});

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[var(--color-hairline)] px-5 py-4">
      <div>
        <h2 className="text-base font-semibold text-[var(--color-ink)]">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-xs text-[var(--color-muted)]">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'critical' | 'high' | 'medium' | 'low' | 'info' | 'success' | 'brand';
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-[var(--color-surface-elevated)] text-[var(--color-body)]',
    critical: 'bg-[color-mix(in_srgb,var(--color-error)_18%,transparent)] text-[var(--color-error)]',
    high: 'bg-[color-mix(in_srgb,var(--color-warning)_18%,transparent)] text-[var(--color-warning)]',
    medium: 'bg-[color-mix(in_srgb,#eab308_18%,transparent)] text-[#eab308]',
    low: 'bg-[color-mix(in_srgb,var(--color-accent-blue)_18%,transparent)] text-[var(--color-accent-blue)]',
    info: 'bg-[var(--color-surface-elevated)] text-[var(--color-muted)]',
    success:
      'bg-[color-mix(in_srgb,var(--color-success)_18%,transparent)] text-[var(--color-success)]',
    brand: 'bg-[var(--color-primary)] text-[var(--color-on-primary)]',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      <p className="text-sm font-semibold text-[var(--color-ink)]">{title}</p>
      {description ? (
        <p className="max-w-md text-sm text-[var(--color-muted)]">{description}</p>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-xs text-[var(--color-muted)]">
      <span className="size-3.5 animate-spin rounded-full border-2 border-[var(--color-hairline-strong)] border-t-[var(--color-primary)]" />
      {label ?? 'Carregando…'}
    </div>
  );
}

/** Loading ou erro de query — evita confundir falha de rede com lista vazia. */
export function QueryState({
  isLoading,
  isError,
  loadingLabel,
  errorTitle = 'Não foi possível carregar',
  errorDescription,
  onRetry,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  loadingLabel?: string;
  errorTitle?: string;
  errorDescription?: string;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (isLoading) return <Spinner label={loadingLabel} />;
  if (isError) {
    return (
      <EmptyState
        title={errorTitle}
        description={errorDescription ?? 'Verifique a conexão e tente de novo.'}
        action={
          onRetry ? (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Tentar de novo
            </Button>
          ) : undefined
        }
      />
    );
  }
  return <>{children}</>;
}

/** Número grande em amarelo — signature do DESIGN.md para métricas. */
export function Stat({
  label,
  value,
  hint,
  size = 'lg',
  valueTone = 'primary',
}: {
  label: string;
  value: string;
  hint?: string;
  /** `md` para valores longos (moeda, percentuais compostos). */
  size?: 'lg' | 'md';
  /** `primary` = amarelo signature; tons de score usam a faixa 0–100. */
  valueTone?: 'primary' | ScoreTone;
}) {
  const valueColor =
    valueTone === 'primary'
      ? 'text-[var(--color-primary)]'
      : SCORE_TONE_VALUE_CLASS[valueTone];

  return (
    <Card className="px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-[1.5px] text-[var(--color-muted)]">
        {label}
      </p>
      <p
        className={cn(
          'mt-2 font-bold tracking-tight tabular-nums',
          valueColor,
          size === 'md'
            ? 'text-2xl sm:text-3xl'
            : 'text-4xl sm:text-5xl',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-[11px] text-[var(--color-muted)]">{hint}</p> : null}
    </Card>
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'danger',
  isPending,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  isPending?: boolean;
  onConfirm(): void;
  onCancel(): void;
}) {
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-black/70"
        onClick={onCancel}
      />
      <Card
        className="relative z-10 w-full max-w-md p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <h2 id={titleId} className="text-lg font-bold tracking-tight text-[var(--color-ink)]">
          {title}
        </h2>
        <p id={descriptionId} className="mt-2 text-sm text-[var(--color-body)]">
          {description}
        </p>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? 'Confirmando…' : confirmLabel}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function Dialog({
  open,
  title,
  description,
  onClose,
  children,
  size = 'md',
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose(): void;
  children: ReactNode;
  size?: 'md' | 'lg';
}) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />
      <Card
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          'relative z-10 flex max-h-[92dvh] w-full flex-col overflow-hidden outline-none',
          size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg',
        )}
      >
        <div className="border-b border-[var(--color-hairline)] px-5 py-4">
          <h2 id={titleId} className="text-lg font-bold tracking-tight text-[var(--color-ink)]">
            {title}
          </h2>
          {description ? (
            <p id={descriptionId} className="mt-1 text-sm text-[var(--color-muted)]">
              {description}
            </p>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </Card>
    </div>
  );
}

export function ScoreBadge({ score }: { score: number }) {
  return <Badge tone={scoreTone(score)}>{score.toFixed(0)}</Badge>;
}

export const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Médio',
  low: 'Baixo',
  info: 'Informativo',
};

export const FIELD_LABEL: Record<string, string> = {
  name: 'Nome (catálogo)',
  title: 'Title (SEO)',
  description: 'Descrição',
  metaTagDescription: 'Meta description',
  keywords: 'Palavras-chave',
  specifications: 'Especificações',
  imageAltText: 'Alt text de imagem',
  imageLabel: 'Label de imagem',
  skuName: 'Nome do SKU',
};

/** Tooltip leve para hover/foco (útil também em chips desabilitados no mobile). */
export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className="z-50 max-w-xs rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-elevated)] px-2.5 py-1.5 text-[11px] leading-snug text-[var(--color-ink)]"
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-[var(--color-surface-elevated)]" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
